import React from "react";
import {
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TableHead,
  Paper,
  TableFooter,
  TablePagination,
  IconButton,
  Box,
  SvgIcon,
  Tooltip
} from "@mui/material";
import { JOB_LIMIT } from "../../../helpers/constants";
import TablePaginationActions from "./TablePaginationActions";
import { Link, useNavigate } from "react-router-dom";
import { formatDateTime } from "../../../helpers/cookies";
import SendIcon from "@mui/icons-material/Send";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";

const EditSvgIcon = (props) => (
  <SvgIcon {...props}>
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </g>
  </SvgIcon>
);

function JobTable({
  data = [],
  loading = false,
  total = 0,
  page = 0,
  onPageChange,
  job_id,
  onSendProof,
  onAssign,
  selected_process_tab,
  company_process,
  columns: providedColumns = []
}) {
  const navigate = useNavigate();
  const dataKeys =
    data.length > 0 ? Object.keys(data[0]).filter(k => !k.startsWith("_")) : [];


  const columns = [
    ...providedColumns,                              // your saved order first
    ...dataKeys.filter((k) => !providedColumns.includes(k)), // append missing keys
  ];

  // Define custom column order
  const customOrder = [
    "id",
    "submitted_date",
    "chefworks_order_number",
    "submitter",
    "rush",
    "request_type",
  ];

  // 2) key -> label (DISPLAY)
  const columnLabels = {
    id: "Job #",
    submitted_date: "Date Submitted",
    chefworks_order_number: "ChefWorks Order #",
    submitter: "Submitter",
    rush: "Rush",
    request_type: "Request Type",
  };

  // 3) stable sort using rank
  const rank = Object.fromEntries(customOrder.map((k, i) => [k, i]));

  columns.sort((a, b) => {
    const ra = rank[a];
    const rb = rank[b];
    const aIn = ra !== undefined;
    const bIn = rb !== undefined;

    if (aIn && bIn) return ra - rb;
    if (aIn) return -1;
    if (bIn) return 1;
    return 0;
  });

  const getFileName = (url) => {
    if (!url) return "Unknown File";
    try {
      return decodeURIComponent(url.split("/").pop());
    } catch {
      return url.split("/").pop();
    }
  };

  // YOU CAN ADJUST COLUMN WIDTHS HERE
  const getColumnWidth = (col) => {
    const lowerCol = col.toLowerCase();

    if (col === 'id') return '60px'; // Compact Job #
    if (lowerCol.includes('reference') || lowerCol === 'ref_no') return '160px'; // Wider for PO numbers
    if (lowerCol.includes('status')) return '140px';
    if (lowerCol.includes('priority')) return '110px';
    if (lowerCol.includes('service') || lowerCol.includes('category')) return '170px'; // "Embroidery Digitizing" etc.
    if (lowerCol === 'description') return '350px'; // Main wide column
    if (lowerCol.includes('submit') || lowerCol.includes('date') || lowerCol.includes('updated')) return '150px'; // Date columns

    return '130px'; // Default width for other columns
  };

  const getProcessSettings = (processId) => {
    if (!company_process) return { standard: false, annotation: false };
    // Use loosely equal to match string/number IDs
    const process = company_process.find(p => p.id == processId);
    return {
      standard: process?.standard_proof || false,
      annotation: process?.annotated_proof || false
    };
  };

  return (
    <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: 0 }}>
      {/* ... existing TableContainer ... */}
      <TableContainer sx={{ maxHeight: { xl: 800, md: 440 } }}>
        <Table stickyHeader aria-label="sticky table" size="small" sx={{ tableLayout: "auto", px: 2 }}>
          <TableHead>
            {/* ... existing TableHead content ... */}
            <TableRow>
              {columns.filter(c => c !== 'is_elapsed').map((col) => (
                <TableCell
                  key={col}
                  sx={{
                    backgroundColor: "#fff",
                    color: "#4b5563",
                    fontSize: "16px",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    fontFamily: "'Inter Tight', sans-serif",
                    borderBottom: "2px solid gray",
                    textAlign: "left",
                    pl: 1.5,
                    pr: 1.5,
                    width: col === "Description" ? "350px" : "auto",
                    minWidth: col === "Description" ? "350px" : "auto",
                    maxWidth: col === "Description" ? "350px" : "unset",
                    verticalAlign: "middle",
                  }}
                >
                  {columnLabels[col] ?? col}
                </TableCell>
              ))}

              {/* Edit column */}
              <TableCell

                sx={{
                  backgroundColor: "#fff",
                  color: "#4b5563",
                  fontFamily: "'Inter Tight', sans-serif",
                  borderBottom: "2px solid gray",
                  textAlign: "center",
                  width: "100px",
                  minWidth: "100px",
                  verticalAlign: "middle",
                }}
              >

              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {data.length > 0 ? (
              data.map((row, idx) => {
                // Check process settings for this row's process
                const settings = getProcessSettings(row.process_id || selected_process_tab);
                const shouldShowSendProof = !!(settings.standard || settings.annotation);

                const isElapsedRow = Boolean(row.is_elapsed) === true && row._stage_name?.toLowerCase().trim() !== 'completed';

                return (
                  <TableRow
                    hover
                    key={idx}
                    sx={{
                      "&:nth-of-type(odd)": { backgroundColor: "white" },
                    }}
                  >
                    {columns.filter(c => c !== 'is_elapsed').map((col, colIdx) => (
                      <TableCell
                        key={col}
                        sx={{
                          textAlign: "left",
                          pl: colIdx === 0 ? (isElapsedRow ? '6px' : '12px') : 1.5,
                          pr: 1.5,
                          fontFamily: "'Inter Tight', sans-serif",
                          fontSize: "15px",
                          color: "black",
                          width: col === "Description" ? "150px" : "auto",
                          minWidth: col === "Description" ? "150px" : "auto",
                          maxWidth: col === "Description" ? "150px" : "unset",
                          verticalAlign: "top",
                          ...(colIdx === 0 && isElapsedRow ? { borderLeft: '5px solid #d32f2f' } : {}),
                        }}
                      >
                        {col === "Description" ? (
                          <Tooltip title={row[col]} placement="top" arrow>
                            <div style={{
                              wordBreak: "break-word",
                              whiteSpace: "normal",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              display: "-webkit-box",
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: "vertical",
                            }}>
                              {row[col]}
                            </div>
                          </Tooltip>
                        ) : (
                          <div style={{
                            wordBreak: "break-word",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis"
                          }}>
                            {col === "id" ? (
                              <Link to={selected_process_tab ? `/job-detail/${row[col]}?process_id=${selected_process_tab}` : `/job-detail/${row[col]}`} style={{ color: "black", textDecoration: 'none' }}>
                                {row[col]}
                              </Link>
                            ) : col === "Need a quote" ? (
                              row[col] ? "Yes" : "No"
                            ) : Array.isArray(row[col]) ? (
                              row[col].length > 0 ? (
                                row[col].map((fileUrl, i) => (
                                  <div key={i}>
                                    <a href={fileUrl} target="_blank" rel="noopener noreferrer">
                                      {getFileName(fileUrl)}
                                    </a>
                                  </div>
                                ))
                              ) : "—"
                            ) : (col.toLowerCase().includes("date") ||
                              col.toLowerCase().includes("submitted") ||
                              col.toLowerCase().includes("updated") ||
                              (typeof row[col] === 'string' && /^\d{4}-\d{2}-\d{2}/.test(row[col]))) && row[col] ? (
                              formatDateTime(row[col])
                            ) : row[col] ? (
                              row[col]
                            ) : (
                              "—"
                            )}
                          </div>
                        )}
                      </TableCell>
                    ))}

                    {/* Edit button cell */}
                    <TableCell
                      align="center"
                      sx={{
                        width: "100px",
                        minWidth: "100px",
                        verticalAlign: "top",
                      }}
                    >
                      <Box sx={{ display: "flex", gap: "6px", justifyContent: "center" }}>

                        {/* Edit Icon */}
                        <IconButton
                          onClick={() => navigate(`/update-job/${row.id}`)}
                          sx={{
                            width: 32,
                            height: 32,
                            backgroundColor: "#ff6100",
                            borderRadius: "6px",
                            color: "#fff",
                            "&:hover": { backgroundColor: "#e65500" },
                          }}
                        >
                          <EditSvgIcon sx={{ fontSize: 18 }} />
                        </IconButton>



                        {/* Rotated Send Icon */}
                        {row._stage_name?.toLowerCase().trim() === 'completed' && row._has_pdf && shouldShowSendProof && (
                          <IconButton
                            onClick={() => onSendProof && onSendProof(row.id)}
                            sx={{
                              width: 32,
                              height: 32,
                              backgroundColor: "#ff6100",
                              borderRadius: "6px",
                              color: "#fff",
                              "&:hover": { backgroundColor: "#e65500" },
                            }}
                          >
                            <SendIcon
                              sx={{
                                fontSize: 18,
                                transform: "rotate(-45deg)",
                              }}
                            />
                          </IconButton>
                        )}

                        {/* Assign button — visible when onAssign is provided */}
                        {onAssign && (
                          <Tooltip title="Assign to user" arrow>
                            <IconButton
                              onClick={() => onAssign(row.id)}
                              sx={{
                                width: 32,
                                height: 32,
                                backgroundColor: "#3b82f6",
                                borderRadius: "6px",
                                color: "#fff",
                                "&:hover": { backgroundColor: "#2563eb" },
                              }}
                            >
                              <PersonAddAlt1Icon sx={{ fontSize: 17 }} />
                            </IconButton>
                          </Tooltip>
                        )}

                      </Box>
                    </TableCell>

                  </TableRow>
                );
              })
            ) : !loading ? (
              <TableRow>
                <TableCell align="center" colSpan={columns.length + 1}>
                  No Record Found
                </TableCell>
              </TableRow>
            ) : (
              <TableRow>
                <TableCell align="center" colSpan={columns.length + 1}>
                  Loading...
                </TableCell>
              </TableRow>
            )}
          </TableBody>

          {data.length > 0 && (
            <TableFooter>
              <TableRow>
                <TableCell colSpan={columns.length + 1}>
                  <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    rowsPerPage={JOB_LIMIT}
                    rowsPerPageOptions={[JOB_LIMIT]}
                    onPageChange={onPageChange}
                    ActionsComponent={TablePaginationActions}
                    labelDisplayedRows={() => null}
                  />
                </TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>
    </Paper>
  );
}

export default JobTable;
